import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../context/AuthContext";

const STORAGE_KEY = "gf_chat_session";

function getAnonId() {
  let sid = localStorage.getItem(STORAGE_KEY);
  if (!sid) {
    sid = "g-" + Math.random().toString(36).slice(2, 10);
    localStorage.setItem(STORAGE_KEY, sid);
  }
  return sid;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [assistantName, setAssistantName] = useState("BookFreely AI");
  const { authHeader } = useAuth();
  const scrollRef = useRef(null);
  const anonId = getAnonId();

  useEffect(() => {
    axios.get(`${API}/platform/config`)
      .then((r) => { if (r.data?.ai_assistant_name) setAssistantName(r.data.ai_assistant_name); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    const greeting = `Hi, I'm ${assistantName}, the BookFreely assistant. Ask me anything about how the app works, or tell me what's going wrong and I'll help figure out the best path forward.`;
    const shortFallback = `Hi, I'm ${assistantName}. How can I help?`;
    axios.get(`${API}/chat/history`, { params: { session_id: anonId }, headers: authHeader() })
      .then((r) => {
        if (r.data && r.data.length) setMessages(r.data);
        else setMessages([{ role: "assistant", text: greeting }]);
      })
      .catch(() => setMessages([{ role: "assistant", text: shortFallback }]));
    // eslint-disable-next-line
  }, [open, assistantName]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const t = input.trim();
    if (!t || sending) return;
    setMessages((m) => [...m, { role: "user", text: t }]);
    setInput("");
    setSending(true);
    try {
      const r = await axios.post(`${API}/chat`, { message: t, session_id: anonId }, { headers: authHeader() });
      setMessages((m) => [...m, { role: "assistant", text: r.data.text }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", text: "I'm having trouble reaching the assistant right now. Please try again in a moment." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-toggle-btn"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-12 right-6 z-50 rounded-full shadow-lg w-14 h-14 flex items-center justify-center font-medium"
        style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
        aria-label="Open chat"
      >
        {open ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
        )}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-32 right-6 z-50 w-[360px] max-w-[92vw] rounded-2xl border shadow-2xl flex flex-col"
          style={{ background: "var(--gf-surface)", borderColor: "var(--gf-border-strong)", height: 480 }}
        >
          <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--gf-border)" }}>
            <div>
              <div className="font-display text-lg" style={{ color: "var(--gf-text)" }} data-testid="chat-assistant-name">{assistantName}</div>
              <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-accent)" }} data-testid="chat-widget-tagline">BookFreely AI · Always here to help</div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={"flex " + (m.role === "user" ? "justify-end" : "justify-start")}>
                <div
                  className="max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-line"
                  style={
                    m.role === "user"
                      ? { background: "var(--gf-accent)", color: "var(--gf-on-accent)" }
                      : { background: "var(--gf-surface-2)", color: "var(--gf-text)", border: "1px solid var(--gf-border)" }
                  }
                >
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="rounded-2xl px-3 py-2 text-sm border" style={{ background: "var(--gf-surface-2)", borderColor: "var(--gf-border)", color: "var(--gf-text-muted)" }}>
                  Thinking…
                </div>
              </div>
            )}
          </div>

          <form onSubmit={send} className="p-3 border-t flex gap-2" style={{ borderColor: "var(--gf-border)" }}>
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question or report a problem..."
              className="flex-1 bg-transparent border rounded-full px-4 py-2 text-sm outline-none focus:border-[var(--gf-accent)]"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            />
            <button data-testid="chat-send-btn" disabled={sending || !input.trim()} className="rounded-full px-4 py-2 text-sm font-medium disabled:opacity-50"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
              Send
            </button>
          </form>
        </div>
      )}
    </>
  );
}
